import Dexie, { type EntityTable } from 'dexie';
import type { Contact, ContactDrafts, LogEntry, Person, SelectOption, Synced, User } from '@crmsy/shared';

/** Browser replica (§4.5). Stores mirror the server's; `outbox` and `meta` are local. */
export interface SchemaRow {
  property: string;
  options: SelectOption[];
}

/** A queued action (§5.4, §5.7). `snapshot` is the contact before the optimistic change, for rollback on 4xx. */
export interface OutboxItem {
  actionId: string;
  type: string;
  payload: unknown;
  expected: Record<string, unknown>;
  at: string;
  status: 'queued' | 'sending' | 'failed';
  error?: string;
  attempts: number;
  /** The contact this action touches (a temp id for createProspect). */
  contactId: string | null;
  snapshot: Synced<Contact> | null;
  /** Log entries archived/restored optimistically, with their previous flag, for rollback. */
  logSnapshot: { id: string; archived: boolean; reverted?: boolean }[];
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class CrmDb extends Dexie {
  contacts!: EntityTable<Synced<Contact>, 'id'>;
  log!: EntityTable<Synced<LogEntry>, 'id'>;
  schema!: EntityTable<SchemaRow, 'property'>;
  people!: EntityTable<Person, 'id'>;
  drafts!: EntityTable<Synced<ContactDrafts>, 'contactId'>;
  outbox!: EntityTable<OutboxItem, 'actionId'>;
  meta!: EntityTable<MetaRow, 'key'>;

  constructor() {
    super('crmsy');
    this.version(1).stores({
      contacts: 'id, rev, archived',
      log: 'id, rev, contactId',
      schema: 'property',
      people: 'id',
      outbox: 'actionId, status',
      meta: 'key',
    });
    this.version(2).stores({
      log: 'id, rev, contactId, actionId',
      outbox: 'actionId, status, contactId',
    });
    this.version(3).stores({
      drafts: 'contactId, rev',
    });
  }
}

export const db = new CrmDb();

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function getLastRev(): Promise<number> {
  return (await getMeta<number>('lastRev')) ?? 0;
}

export async function getCachedUser(): Promise<User | undefined> {
  return getMeta<User>('user');
}

/** Id of the provisional log row the outbox writes for an action (replaced by the server's row on pull). */
export const provisionalLogId = (actionId: string) => `pending:${actionId}`;

/** Wipe the replica (sign-out, or a server that asked for a full pull). Keeps the outbox: queued actions still replay. */
export async function clearReplica(): Promise<void> {
  await db.transaction('rw', [db.contacts, db.log, db.schema, db.people, db.drafts, db.meta], async () => {
    await Promise.all([db.contacts.clear(), db.log.clear(), db.schema.clear(), db.people.clear(), db.drafts.clear(), db.meta.delete('lastRev')]);
  });
}

/** Sign-out: everything, including pending actions. */
export async function clearAll(): Promise<void> {
  await clearReplica();
  await db.outbox.clear();
  await db.meta.clear();
}
